import RenderHTML from '../../../../../../../../components/ui-components/RenderHTML'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import type { LumpsumRebalanceBuyRecommendation } from '../../../../api/rebalance.types'
import { PlanCategoryBucketKey } from '../../../../api/types'
import { useOpenPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenPlanRecommendationBottomSheet'
import { usePlanBuyAction } from '../../../../../execution/buy/usePlanBuyAction'
import { useLumpsumPlansContext } from '../../../LumpsumPlansProvider'
import {
  PlanFundInsightVisual,
  PlansFundCard,
  type PlanFundCardCta,
  type PlanFundCardInsight
} from '../../../lumpsum-ui-components/PlansFundCard'
import {
  ALLOCATION_BUY_FUND_CARD_INVEST_MORE_ICON_SVG,
  ALLOCATION_BUY_FUND_CARD_NEW_FUND_ICON_SVG
} from '../../allocation/cards/AllocationBuyFundCard'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type RebalanceBuyFundCardProps = {
  recommendation: LumpsumRebalanceBuyRecommendation
  showBottomDivider?: boolean
}

export function RebalanceBuyFundCard({
  recommendation,
  showBottomDivider = true
}: RebalanceBuyFundCardProps) {
  const { openPlanRecommendationBottomSheet } = useOpenPlanRecommendationBottomSheet()
  const { rebalancePlansData } = useLumpsumPlansContext()
  const { onBuy } = usePlanBuyAction({ transactionCta: rebalancePlansData?.transactionCta })
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.LumpsumRebalance
  const fundRole = PlansFundRole.Buy
  const buyRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: recommendation?.buyAmount ?? 0 })

  const openPlanBottomSheet = () => {
    openPlanRecommendationBottomSheet({
      isin,
      bucketType: PlanCategoryBucketKey.buyRecommendations
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-action`,
      label: 'Buy',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Buy', fundName, fundIsin: isin })
        onBuy({
          isin,
          fundName,
          amount: buyRecommendationFormattedAmount.value
        })
      },
      variant: 'tertiary'
    },
    {
      id: `${isin}-view-plan`,
      label: 'View plan',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'View plan', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<RebalanceBuyFundCardTrailing recommendation={recommendation} />}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      recommendationStatus={recommendation?.recommendationStatus}
      recommendationStatusDisplayText={recommendation?.recommendationStatusDisplayText}
      ctas={ctas}
      insights={buildRebalanceBuyInsights(recommendation)}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}

export function buildRebalanceBuyInsights(
  recommendation: LumpsumRebalanceBuyRecommendation
): PlanFundCardInsight[] {
  const isin = recommendation?.isin ?? ''
  const insights: PlanFundCardInsight[] = []

  /* Backend is gonna send underPerformance as true for buy also because for sell this is the case
  but in this context, we need to show it as better performing */
  if (recommendation?.underPerformance) {
    insights.push({
      id: `${isin}-rebalance-under-performance`,
      text: 'Better performing',
      visual: PlanFundInsightVisual.POSITIVE
    })
  }
  /* Backend is gonna send highExposure as true for buy also because for sell this is the case
  but in this context, we need to show it as increase exposure */
  if (recommendation?.highExposure) {
    insights.push({
      id: `${isin}-rebalance-high-exposure`,
      text: 'Increase exposure',
      visual: PlanFundInsightVisual.POSITIVE
    })
  }

  return [] as PlanFundCardInsight[]
}

export function RebalanceBuyFundCardTrailing({
  recommendation
}: {
  recommendation: LumpsumRebalanceBuyRecommendation
}) {
  const buyRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: recommendation?.buyAmount ?? 0 })
  const fundMovement = resolveRebalanceBuyFundMovement(recommendation?.currentValue ?? 0)

  return (
    <div className="flex shrink-0 flex-col items-end gap-0">
      <div className="text-[14px] font-[500] leading-[150%] text-[#232323] whitespace-nowrap">
        Buy {buyRecommendationFormattedAmount.label}
      </div>
      <div className="flex items-center justify-end gap-1 py-1 pl-1.5">
        <RenderHTML
          innerHTMLText={fundMovement.iconHtml}
          className="size-3 shrink-0 overflow-hidden"
        />
        <div className="text-[13px] font-[400] leading-[140%] text-[#3D3D3D] whitespace-nowrap">
          {fundMovement.text}
        </div>
      </div>
    </div>
  )
}

function resolveRebalanceBuyFundMovement(
  currentValue: number
): { iconHtml: string; text: string } {
  if (currentValue <= 0) {
    return {
      iconHtml: ALLOCATION_BUY_FUND_CARD_NEW_FUND_ICON_SVG,
      text: 'New fund'
    }
  }

  return {
    iconHtml: ALLOCATION_BUY_FUND_CARD_INVEST_MORE_ICON_SVG,
    text: 'Invest more'
  }
}
